import {useEffect, useRef, useState} from "react";
import {Box} from "@mui/material";
import {Button, Container, DebtLogo, Entrance, Text} from "~/new-design/components";
import {DATE_FORMAT_V2, formatCurrency, getAccountCardLabel} from "@bounce/shared/helpers";
import {AccountCard} from "./AccountCard";
import {getDebtHeader} from "~/helpers/debtHeader";
import {getVerticalDetails} from "~/helpers/account";
import welcomeLandscape from "~/assets/welcome-landscape.jpg";
import welcomeLandscapeDesktop from "~/assets/welcome-landscape-desktop.jpg";
import {useAccount, useBestSavings, useBundleAccounts} from "~/new-design/hooks";
import {colors, layout, spacing} from "~/new-design/theme";
import {AccountExternal, PreLoginContext} from "@bounce/shared/consumer/declarations";
import dayjs from "dayjs";
import {useConsumerUser} from "~/contexts";
import {EducationalFlowLink} from "./EducationalFlowLink";

type WelcomeHeroSectionProps = {
  onViewOptions: () => void;
  onViewDetails: () => void;
  onEducationalFlow?: () => void;
};

const HERO_HEIGHT_RATIO = 0.9;

export const buildAccountDetails = (account?: AccountExternal, preLogin?: PreLoginContext) => {
  const creditorName = account?.partner_marketing_name || preLogin?.partner_marketing_name || "";
  const originalCreditor = account?.original_creditor || preLogin?.original_creditor || "";
  const openDate = preLogin?.origination_date || account?.origination_date;
  const formattedOpenDate = dayjs(openDate).format(DATE_FORMAT_V2);

  return [
    ...getVerticalDetails(account ?? preLogin ?? {}),
    ...(creditorName ? [{label: "Current creditor", value: creditorName}] : []),
    ...(originalCreditor ? [{label: "Original creditor", value: originalCreditor}] : []),
    ...(openDate ? [{label: "Open date", value: formattedOpenDate || ""}] : []),
    ...(account?.external_account_id || preLogin?.external_account_id
      ? [{label: "Account number", value: account?.external_account_id || preLogin?.external_account_id || ""}]
      : []),
    ...(account?.public_id || preLogin?.public_account_id
      ? [{label: "Reference number", value: account?.public_id || preLogin?.public_account_id || ""}]
      : []),
  ];
};

export const WelcomeHeroSection = ({onViewOptions, onViewDetails, onEducationalFlow}: WelcomeHeroSectionProps) => {
  const {userContext} = useConsumerUser();
  const {account} = useAccount();
  const {lowestMonthlyInstallment} = useBestSavings();
  const {isBundled, bundleAccounts, lowestBundleInstallment, totalBundleBalance} = useBundleAccounts();

  const {pre_login_context: preLogin, user_name: userName} = userContext ?? {};

  const accountStatus = getAccountCardLabel({
    inDispute: !!account?.active_dispute,
    inVerificationRequest: false,
    creditReportDetails: account?.credit_report_details,
    paymentPlan: account?.active_payment_plan,
    isCreditRecalled: account?.status === "creditor_recalled",
    isClosed: account?.status === "closed",
    isReturned: account?.status === "returned",
    isPaid: account?.status === "paid",
  });
  const isResolved = accountStatus.label === "Resolved";

  const isDelinquent = !!account?.collects_delinquent_amounts;
  const creditorName = account?.partner_marketing_name || preLogin?.partner_marketing_name || "";
  const balance = formatCurrency(
    (isBundled ? totalBundleBalance : account?.total_balance) ?? account?.total_balance ?? preLogin?.balance ?? 0
  );
  const details = buildAccountDetails(account, preLogin);
  const partnerLogoUrl = preLogin?.partner_symbol_url || account?.partner_symbol_url;
  const vertical = account ? account.vertical : preLogin?.vertical;
  const subProduct = account ? account.sub_product : preLogin?.sub_product;
  const debtHeader = getDebtHeader({vertical, subProduct, partnerMarketingName: creditorName});
  const showDebtHeader = debtHeader.hasSubProduct && !isBundled;

  const inlineCtaRef = useRef<HTMLDivElement>(null);
  const [showFloating, setShowFloating] = useState(false);
  const [viewportHeight, setViewportHeight] = useState(() => (typeof window === "undefined" ? 0 : window.innerHeight));

  useEffect(() => {
    const el = inlineCtaRef.current;
    if (!el) return;
    const observer = new IntersectionObserver(([entry]) => setShowFloating(!entry.isIntersecting));
    observer.observe(el);
    return () => observer.disconnect();
  }, []);

  useEffect(() => {
    const measure = () => setViewportHeight(window.innerHeight);
    measure();
    window.addEventListener("orientationchange", measure);
    return () => window.removeEventListener("orientationchange", measure);
  }, []);

  return (
    <Container sx={{position: "relative", minHeight: viewportHeight ? viewportHeight * HERO_HEIGHT_RATIO : "90svh"}}>
      <Box
        sx={{
          position: "absolute",
          top: 0,
          bottom: 0,
          left: "50%",
          width: "100vw",
          transform: "translateX(-50%)",
          zIndex: 0,
          backgroundImage: `url(${welcomeLandscape})`,
          backgroundSize: "cover",
          backgroundPosition: "center",
          backgroundRepeat: "no-repeat",
          maskImage: "linear-gradient(to bottom, #000 75%, transparent)",
          WebkitMaskImage: "linear-gradient(to bottom, #000 75%, transparent)",
          "@media (min-width: 768px)": {
            backgroundImage: `url(${welcomeLandscapeDesktop})`,
          },
        }}
      />

      <Container gap={0} sx={{position: "relative", zIndex: 1, flex: 1}}>
        <Container gap={0} sx={{pt: `${layout.contentTop}px`, flexShrink: 0}}>
          {userName && (
            <Entrance delay={0.5}>
              <Text variant="greeting" color={colors.primary[800]} sx={{mb: spacing[2]}}>
                Welcome, {userName}
              </Text>
            </Entrance>
          )}
          <Entrance delay={0.65}>
            <Text variant="heading-lg" color={colors.primary[800]} component="div">
              Let's{" "}
              <Text variant="emphasis-light" component="span" color={colors.primary[800]}>
                resolve your {isDelinquent ? "delinquent balance" : "balance"}
              </Text>
              <Box>with a plan built around </Box> your life.
            </Text>
          </Entrance>
        </Container>

        <Box sx={{flex: 1, minHeight: spacing[8]}} />

        <Entrance delay={1.1} variant="slide" sx={{flexShrink: 0}}>
          <Container gap={5}>
            <AccountCard
              creditorName={showDebtHeader ? debtHeader.title : creditorName}
              subtitle={showDebtHeader ? debtHeader.subtitle : undefined}
              logo={
                showDebtHeader ? (
                  <DebtLogo
                    vertical={vertical}
                    subProduct={subProduct}
                    partnerLogoUrl={partnerLogoUrl}
                    partnerName={creditorName}
                  />
                ) : undefined
              }
              partnerLogoUrl={partnerLogoUrl}
              balance={balance}
              status={{label: accountStatus.label, variant: accountStatus.variant}}
              details={details}
              lowestMonthlyInstallment={isBundled ? lowestBundleInstallment : lowestMonthlyInstallment}
              bundleAccounts={bundleAccounts}
              partnerCode={account?.partner_code}
              onViewDetails={onViewDetails}
            />
            {!isResolved && (
              <Box ref={inlineCtaRef} sx={{display: "flex", flexDirection: "column", width: "100%", gap: spacing[2]}}>
                <Button fullWidth onClick={onViewOptions}>
                  View payment options
                </Button>
                {onEducationalFlow ? (
                  <EducationalFlowLink onClick={onEducationalFlow} />
                ) : (
                  <Text variant="caption-medium" color={colors.grey[600]} sx={{textAlign: "center"}}>
                    No fees. Start within 30 days, cancel anytime.
                  </Text>
                )}
              </Box>
            )}
          </Container>
        </Entrance>
      </Container>

      {!isResolved && (
        <Box
          sx={{
            position: "fixed",
            bottom: 0,
            left: 0,
            right: 0,
            zIndex: 10,
            display: "flex",
            justifyContent: "center",
            px: spacing[4],
            pb: spacing[6],
            pointerEvents: "none",
            opacity: showFloating ? 1 : 0,
            transform: showFloating ? "translateY(0)" : "translateY(12px)",
            transition: "opacity 0.25s ease, transform 0.25s ease",
          }}
        >
          <Box sx={{width: "100%", maxWidth: layout.maxWidth, pointerEvents: showFloating ? "auto" : "none"}}>
            <Button fullWidth onClick={onViewOptions}>
              View payment options
            </Button>
          </Box>
        </Box>
      )}
    </Container>
  );
};
